import React, { useEffect, useState } from "react";
import { fetchClientRestriction, updateClientRestriction } from "../services/clientRestrictions";

const MODULE_OPTIONS = [
    { key: "requests", label: "Заявки" },
    { key: "polls", label: "Опитування" },
    { key: "feedbacks", label: "Відгуки" },
    { key: "cars", label: "Авто" },
];

const MUTE_PRESETS = [3, 7, 14, 30];

const ClientRestrictionPanel = ({ clientId, onChanged }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [reason, setReason] = useState("");
    const [modules, setModules] = useState([]);
    const [muteDays, setMuteDays] = useState(3);

    const load = async () => {
        if (!clientId) return;
        setLoading(true);
        setError("");
        try {
            const res = await fetchClientRestriction(clientId);
            setData(res);
            setReason(res.reason || "");
            setModules(Array.isArray(res.readonly_modules) ? res.readonly_modules : []);
            setMuteDays(res.next_mute_days || 3);
        } catch (e) {
            setError(e?.message || "Помилка завантаження");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    const toggleModule = (key) => {
        setModules((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
    };

    const saveModules = async () => {
        setSaving(true);
        setError("");
        try {
            const res = await updateClientRestriction(clientId, {
                readonly_modules: modules,
                reason: reason || null,
            });
            setData(res);
            if (typeof onChanged === "function") onChanged(res);
        } catch (e) {
            setError(e?.message || "Помилка збереження");
        } finally {
            setSaving(false);
        }
    };

    const applyMute = async () => {
        setSaving(true);
        setError("");
        try {
            const res = await updateClientRestriction(clientId, {
                chat_mute_days: Number(muteDays),
                escalate_strike: true,
                reason: reason || null,
            });
            setData(res);
            setMuteDays(res.next_mute_days || muteDays);
            if (typeof onChanged === "function") onChanged(res);
        } catch (e) {
            setError(e?.message || "Помилка муту");
        } finally {
            setSaving(false);
        }
    };

    const clearMute = async () => {
        setSaving(true);
        setError("");
        try {
            const res = await updateClientRestriction(clientId, {
                clear_chat_mute: true,
                reason: reason || null,
            });
            setData(res);
            if (typeof onChanged === "function") onChanged(res);
        } catch (e) {
            setError(e?.message || "Помилка зняття муту");
        } finally {
            setSaving(false);
        }
    };

    if (loading && !data) {
        return <div className="client-restriction-panel">Завантаження обмежень…</div>;
    }

    const mutedUntil = data?.chat_muted_until
        ? new Date(data.chat_muted_until).toLocaleString("uk-UA")
        : null;

    return (
        <div className="client-restriction-panel" style={{ marginTop: 12, padding: 12, background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Обмеження в мережі</div>
            {error ? <div style={{ color: "#b91c1c", marginBottom: 8, fontSize: 13 }}>{error}</div> : null}

            <div style={{ fontSize: 13, marginBottom: 8, color: "#334155" }}>
                {data?.chat_muted ? (
                    <>🔇 Чат зам'ючено до <strong>{mutedUntil}</strong> (страйк: {data.chat_mute_strike || 0})</>
                ) : (
                    <>Чат активний (страйк: {data?.chat_mute_strike || 0}, наступний мут: {data?.next_mute_days || 3} дн.)</>
                )}
            </div>

            <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Коментар / причина</label>
            <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                style={{ width: "100%", marginBottom: 8, borderRadius: 8, border: "1px solid #cbd5e1", padding: 8 }}
                placeholder="Чому застосовано обмеження"
            />

            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 10 }}>
                <select value={muteDays} onChange={(e) => setMuteDays(Number(e.target.value))} style={{ padding: "6px 8px", borderRadius: 8 }}>
                    {(data?.mute_days_presets || MUTE_PRESETS).map((d) => (
                        <option key={d} value={d}>{d} днів</option>
                    ))}
                </select>
                <button type="button" className="small-btn" disabled={saving} onClick={applyMute}>
                    Зам'ютити чат
                </button>
                {data?.chat_muted ? (
                    <button type="button" className="small-btn" disabled={saving} onClick={clearMute}>
                        Зняти мут
                    </button>
                ) : null}
            </div>

            <div style={{ fontSize: 12, fontWeight: 650, marginBottom: 6 }}>Лише перегляд (без запису)</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                {MODULE_OPTIONS.map((m) => (
                    <label key={m.key} style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 13 }}>
                        <input
                            type="checkbox"
                            checked={modules.includes(m.key)}
                            onChange={() => toggleModule(m.key)}
                        />
                        {m.label}
                    </label>
                ))}
            </div>
            <button type="button" className="small-btn" disabled={saving} onClick={saveModules}>
                Зберегти модулі
            </button>
        </div>
    );
};

export default ClientRestrictionPanel;
